import * as R from 'remeda'

import {
  type ConditionOperation,
  type CustomerAttributeFilterBrandSegments,
  type Question as QuestionBase,
  type QuestionFilter,
  type SegmentFilter,
} from '~/gql/graphql'

import { compact, exists, findByPath, pathOr } from '../generals'

import {
  type SurveyFilterGroups,
  type SurveyFilterItem,
} from './surveyFilterGroups'

type SurveyFilterV1 = {
  questionFilters: QuestionFilter[] | undefined
  segmentFilters: SegmentFilter[] | undefined
}

type SurveyFilterV2 = SurveyFilterGroups

type Question = App.PickGraphQL<QuestionBase, 'answer_options' | 'question_id'>

/**
 * サーベイフィルター ver.1 からサーベイフィルター ver.2 に変換します。
 *
 * @param surveyFilterV1 - サーベイフィルター ver.1 。
 * @param questions - 設問の配列。
 * @returns サーベイフィルター ver.2 。
 */
export function toSurveyFilterV2(
  surveyFilterV1: SurveyFilterV1,
  questions: Question[],
): SurveyFilterV2 {
  const questionFilters = (
    surveyFilterV1.questionFilters ?? []
  ).map<SurveyFilterItem>(({ operation, question_id, values }) => ({
    category: 'Question',
    brandSegments: null,
    customerAttribute: null,
    question: {
      questionId: question_id,
      operation: operation as ConditionOperation,
      answerOptions: R.pipe(
        values ?? [],
        R.map((option) =>
          R.pipe(
            questions,
            findByPath('question_id', question_id),
            pathOr('answer_options', []),
            findByPath('option', option),
          ),
        ),
        compact,
        R.map(R.pick(['option', 'text'])),
      ),
    },
  }))

  const brandSegmentsArray: CustomerAttributeFilterBrandSegments[] = []
  for (const {
    brand_group_id,
    brand_number,
    segment_number,
  } of surveyFilterV1.segmentFilters ?? []) {
    const brandSegments = brandSegmentsArray.find(
      (item) =>
        item.brandGroupId === brand_group_id &&
        item.brandNumber === brand_number,
    )
    if (exists(brandSegments)) {
      brandSegments.segmentNumbers.push(segment_number)
    } else {
      brandSegmentsArray.push({
        brandGroupId: brand_group_id,
        brandNumber: brand_number,
        segmentNumbers: [segment_number],
      })
    }
  }

  const brandSegmentsFilters = brandSegmentsArray.map<SurveyFilterItem>(
    (brandSegments) => ({
      category: 'BrandSegments',
      brandSegments,
      customerAttribute: null,
      question: null,
    }),
  )

  return brandSegmentsFilters.length === 0
    ? questionFilters.length === 0
      ? []
      : [questionFilters]
    : brandSegmentsFilters.map((brandSegmentFilter) => [
        ...questionFilters,
        brandSegmentFilter,
      ])
}
